import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { getPageId, getSiteSlug } from 'services/app';
import { getParentAndChildSlug } from 'services/app/selectors';
import { getDefaultNavigation, getNavigationAppliedInThePage, getNavigationSelectedInTheDropdown } from 'services/navigationv2';
import { makeParentAndChildHashMap } from 'services/navigationv2/utils';
import hash from 'json-stable-stringify';
import { useAdminTranslation } from './use-translation';

export const useNavigation = (id: string) => {
  const { t } = useAdminTranslation();
  const selectedNavigation = useSelector(getNavigationSelectedInTheDropdown);
  const appliedNavigation = useSelector(getNavigationAppliedInThePage);
  const defaultNavigation = useSelector(getDefaultNavigation);
  const navigationData = useMemo(() => {
    if (id === selectedNavigation._id) return selectedNavigation;
    if (id === appliedNavigation._id) return appliedNavigation;
    return defaultNavigation;
  }, [id, hash(selectedNavigation), hash(appliedNavigation), hash(defaultNavigation)]);
  const siteSlug = useSelector(getSiteSlug);
  const currentPageId = useSelector(getPageId);
  const { parentHashMap, childHashMap } = useMemo(() => (makeParentAndChildHashMap(navigationData)), [hash(navigationData)]);
  const { parentHashMap: selectedParentHashMap, childHashMap: selectedChildHashMap } = useMemo(() => (makeParentAndChildHashMap(selectedNavigation)), [hash(selectedNavigation)]);
  const { parentHashMap: defaultParentHashMap, childHashMap: defaultChildHashMap } = useMemo(() => (makeParentAndChildHashMap(defaultNavigation)), [hash(defaultNavigation)]);
  const { parentSlug, childSlug } = useSelector(getParentAndChildSlug);
  const currentNavigation = useMemo(() => (
    parentHashMap.get(currentPageId) || childHashMap.get(currentPageId)
  ),[currentPageId, hash(parentHashMap), hash(childHashMap)]);

  // data related to the current page/channel
  const hasParent = useMemo(() => (!!childSlug), [childSlug, parentSlug]);
  const parentNavigation = useMemo(() => {
    for (const parent of navigationData.parents) {
      const foundChild = parent.children.find((node) => node.id === currentPageId);
      if (foundChild) return parent;
    }
  }, [hash(navigationData.parents), currentPageId]);
  const parentNamePlaceholder = useMemo(() => (
    parentNavigation ? parentNavigation.name : t('ADMIN_LABEL_NONE')
  ),[hash(parentNavigation)]);
  const [parentName, setParentName] = useState(parentNamePlaceholder);

  return {
    appliedNavigation,
    currentNavigation,
    currentPageId,
    childHashMap,
    childSlug,
    defaultNavigation,
    defaultChildHashMap,
    defaultParentHashMap,
    hasParent,
    navigationData,
    parentHashMap,
    parentName,
    parentNavigation,
    parentSlug,
    selectedNavigation,
    selectedParentHashMap,
    selectedChildHashMap,
    setParentName,
    siteSlug,
  };
};
